// Pixel snapping for the brand marks (the wordmark, the coin, the desk icon). Drawn on whole pixels,
// a mark still blurs when the layout places it at a fraction of a pixel (centred beside text, in a
// bar of odd height): each one registered here is nudged by that fraction, onto the screen's pixel
// grid, and again whenever the layout around it changes.
import { useLayoutEffect, type RefObject } from 'react';

const marks = new Set<SVGSVGElement>();
let queued = false;
let watching = false;
let ro: ResizeObserver | null = null;

function snapAll() {
  queued = false;
  const dpr = window.devicePixelRatio || 1;
  const els = [...marks];
  for (const el of els) el.style.translate = ''; // measure where the layout puts it
  const rects = els.map((el) => el.getBoundingClientRect());
  els.forEach((el, i) => {
    const r = rects[i];
    if (!r.width) return;
    const off = (v: number) => (Math.round(v * dpr) - v * dpr) / dpr;
    const dx = off(r.left), dy = off(r.top);
    if (Math.abs(dx) > 0.001 || Math.abs(dy) > 0.001) el.style.translate = `${dx}px ${dy}px`;
  });
}

const schedule = () => {
  if (queued) return;
  queued = true;
  requestAnimationFrame(snapAll);
};

function watch() {
  if (watching) return;
  watching = true;
  ro = new ResizeObserver(schedule);
  ro.observe(document.body);
  addEventListener('resize', schedule);
  document.addEventListener('transitionend', schedule, true);
  document.addEventListener('animationend', schedule, true);
  void document.fonts?.ready.then(schedule);
}

/** Keep this mark on the pixel grid while it is mounted. */
export function usePixelSnap(ref: RefObject<SVGSVGElement | null>) {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    watch();
    marks.add(el);
    // Its own box changing (content beside it) moves it too.
    const parent = el.parentElement;
    if (parent) ro?.observe(parent);
    schedule();
    return () => {
      marks.delete(el);
      if (parent) ro?.unobserve(parent);
    };
  }, [ref]);
}
